// ─── iCloud Key-Value Storage ────────────────────────────────────────────────
// Wraps NSUbiquitousKeyValueStore via native Capacitor plugin.
// Falls back to localStorage when running in browser (dev mode).

import { Capacitor, registerPlugin } from '@capacitor/core';

const ICloudKV = registerPlugin('ICloudKV');

const isNative = Capacitor.isNativePlatform();

// ─── Public API ──────────────────────────────────────────────────────────────

export async function setItem(key, value) {
  // localStorage ZUERST — synchron, ueberlebt garantiert diesen App-Lauf
  try { localStorage.setItem(key, value); } catch {}
  // Dann native (UserDefaults + iCloud) — async, kann fehlschlagen
  if (isNative) {
    try { await ICloudKV.set({ key, value }); } catch {}
  }
}

export async function getItem(key) {
  if (isNative) {
    try {
      const result = await ICloudKV.get({ key });
      const val = result?.value ?? null;
      // If iCloud has data, update localStorage mirror
      if (val != null) {
        try { localStorage.setItem(key, val); } catch {}
      }
      return val;
    } catch {
      // Fallback to localStorage if plugin fails
    }
  }
  try { return localStorage.getItem(key); } catch { return null; }
}

export async function removeItem(key) {
  try { localStorage.removeItem(key); } catch {}
  if (isNative) {
    try { await ICloudKV.remove({ key }); } catch {}
  }
}

// Sync iCloud store (call on app resume)
export async function syncStore() {
  if (isNative) {
    try { await ICloudKV.sync(); } catch {}
  }
}

// ─── Migration: localStorage → iCloud ────────────────────────────────────────
// Run once on first launch after update. Copies existing localStorage data to iCloud.

const MIGRATION_KEY = 'bullivo_icloud_migrated';

export async function migrateToICloud() {
  if (!isNative) return;
  try {
    const already = await getItem(MIGRATION_KEY);
    if (already === '1') return;

    const keysToMigrate = [
      'finanzapp_portfolio_v1',
      'bullivo_user_name',
      'bullivo_onboarding_done',
      'bullivo_tab',
      'bullivo_theme',
      'bullivo_realtime_alerts',
      'bullivo_signal_threshold',
      'bullivo_markets',
      'bullivo_morning_brief',
      'bullivo_afternoon_signal',
      'bullivo_friday_warning',
      'bullivo_show_score',
    ];

    for (const key of keysToMigrate) {
      const val = localStorage.getItem(key);
      if (val != null) {
        await ICloudKV.set({ key, value: val });
      }
    }

    await setItem(MIGRATION_KEY, '1');
    console.log('[iCloud] Migration complete');
  } catch (err) {
    console.warn('[iCloud] Migration error:', err);
  }
}
